import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function UseCasesPage() {
  const useCases = [
    {
      category: 'Optimization',
      color: 'bg-orange-500',
      title: 'Business Process Optimization',
      description: 'Streamline operations with AI that identifies bottlenecks, optimizes workflows, and reduces operational costs by up to 40%.',
      image: 'https://public.readdy.ai/ai/img_res/adbff64dc5a5061a16f1c84053e2fe49.jpg',
      benefits: [
        'Identify process inefficiencies automatically',
        'Optimize resource allocation in real-time',
        'Reduce operational costs by 30-40%',
        'Improve workflow efficiency',
        'Enable data-driven decision making'
      ],
      industries: ['Manufacturing', 'Logistics', 'Healthcare', 'Finance']
    },
    {
      category: 'Automation',
      color: 'bg-orange-600',
      title: 'Intelligent Automation',
      description: 'Deploy smart automation systems that handle complex tasks, learn from interactions, and continuously improve performance.',
      image: 'https://readdy.ai/api/search-image?query=abstract%20modern%20gradient%20visualization%20representing%20intelligent%20automation%20with%20robotic%20process%20flows%20automated%20systems%20and%20smart%20workflows%20on%20clean%20minimal%20background%20professional%20tech%20aesthetic%20smooth%20cyan%20gradients&width=600&height=400&seq=usecase-detail2&orientation=landscape',
      benefits: [
        'Automate repetitive manual tasks',
        'Reduce human error significantly',
        'Scale operations without adding headcount',
        'Improve processing speed by 10x',
        'Free teams for strategic work'
      ],
      industries: ['Banking', 'Insurance', 'Retail', 'Telecommunications']
    },
    {
      category: 'Analytics',
      color: 'bg-red-600',
      title: 'Predictive Insights',
      description: 'Leverage advanced analytics to forecast trends, anticipate market changes, and stay ahead of the competition.',
      image: 'https://readdy.ai/api/search-image?query=abstract%20modern%20gradient%20visualization%20representing%20predictive%20analytics%20with%20data%20charts%20forecasting%20patterns%20and%20trend%20analysis%20on%20clean%20minimal%20background%20professional%20tech%20aesthetic%20smooth%20teal%20gradients&width=600&height=400&seq=usecase-detail3&orientation=landscape',
      benefits: [
        'Forecast demand with 95% accuracy',
        'Predict customer behavior patterns',
        'Identify risks before they materialize',
        'Optimize inventory management',
        'Enable proactive strategy planning'
      ],
      industries: ['E-commerce', 'Supply Chain', 'Marketing', 'Real Estate']
    },
    {
      category: 'Intelligence',
      color: 'bg-orange-700',
      title: 'AI-Driven Decision Making',
      description: 'Empower leadership with real-time intelligence and data-driven recommendations that improve strategic outcomes.',
      image: 'https://readdy.ai/api/search-image?query=abstract%20modern%20gradient%20visualization%20representing%20artificial%20intelligence%20decision%20making%20with%20neural%20pathways%20smart%20systems%20and%20strategic%20insights%20on%20clean%20minimal%20background%20professional%20tech%20aesthetic%20smooth%20green%20gradients&width=600&height=400&seq=usecase-detail4&orientation=landscape',
      benefits: [
        'Real-time business intelligence',
        'Data-driven strategic recommendations',
        'Risk assessment and mitigation',
        'Competitive advantage insights',
        'Improved decision accuracy'
      ],
      industries: ['Consulting', 'Investment', 'Technology', 'Energy']
    },
    {
      category: 'Customer Experience',
      color: 'bg-red-500',
      title: 'Personalized Customer Engagement',
      description: 'Deliver hyper-personalized experiences using AI that understands customer preferences and predicts their needs.',
      image: 'https://readdy.ai/api/search-image?query=abstract%20modern%20gradient%20visualization%20representing%20personalized%20customer%20engagement%20with%20user%20profiles%20interaction%20patterns%20and%20experience%20optimization%20on%20clean%20minimal%20background%20professional%20tech%20aesthetic%20smooth%20purple%20gradients&width=600&height=400&seq=usecase-detail5&orientation=landscape',
      benefits: [
        'Personalize customer interactions at scale',
        'Predict customer needs proactively',
        'Increase customer satisfaction scores',
        'Boost retention rates by 25%',
        'Drive revenue through targeted engagement'
      ],
      industries: ['Retail', 'Hospitality', 'Media', 'Financial Services']
    },
    {
      category: 'Quality Control',
      color: 'bg-orange-800',
      title: 'Automated Quality Assurance',
      description: 'Implement AI-powered quality control systems that detect defects, ensure compliance, and maintain consistent standards.',
      image: 'https://readdy.ai/api/search-image?query=abstract%20modern%20gradient%20visualization%20representing%20automated%20quality%20assurance%20with%20inspection%20systems%20defect%20detection%20and%20compliance%20monitoring%20on%20clean%20minimal%20background%20professional%20tech%20aesthetic%20smooth%20red%20gradients&width=600&height=400&seq=usecase-detail6&orientation=landscape',
      benefits: [
        'Detect defects with 99% accuracy',
        'Reduce quality control costs',
        'Ensure regulatory compliance',
        'Minimize product recalls',
        'Maintain consistent quality standards'
      ],
      industries: ['Manufacturing', 'Pharmaceuticals', 'Food & Beverage', 'Automotive']
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-orange-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-orange-700 text-xs uppercase tracking-wider font-semibold mb-4">
              Real-World Impact
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              AI That Solves<br />Real Problems
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              See how FluxByte's intelligent systems drive measurable outcomes across industries—from 
              operational efficiency to predictive decision-making and customer engagement.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-16">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-full h-80 rounded-2xl overflow-hidden">
                    <img 
                      src={useCase.image} 
                      alt={useCase.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className={`inline-block px-3 py-1 ${useCase.color} text-white text-xs uppercase font-semibold rounded-full mb-4`}>
                    {useCase.category}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {useCase.title}
                  </h2>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">
                    {useCase.description}
                  </p>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Key Benefits
                    </h3>
                    <div className="space-y-2">
                      {useCase.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Industries
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {useCase.industries.map((industry, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:gap-3 transition-all cursor-pointer"
                  >
                    Discuss your project <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Implement AI in Your Business?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Let's explore how these use cases can be adapted to solve your specific challenges.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-600 rounded-full font-semibold text-base hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap"
          >
            Start Your AI Journey →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
